import React, { useState } from 'react';
import { FiFolder, FiChevronDown, FiUpload, FiTrash2, FiEdit2, FiPlus } from 'react-icons/fi';
import { DataRoomFolder, DataRoomDocument } from '../../types/dataRoom';
import DocumentRow from './DocumentRow';

// Type cast icons for React 18 compatibility
const FolderIcon = FiFolder as React.FC<React.SVGProps<SVGSVGElement>>;
const ChevronDownIcon = FiChevronDown as React.FC<React.SVGProps<SVGSVGElement>>;
const UploadIcon = FiUpload as React.FC<React.SVGProps<SVGSVGElement>>;
const TrashIcon = FiTrash2 as React.FC<React.SVGProps<SVGSVGElement>>;
const EditIcon = FiEdit2 as React.FC<React.SVGProps<SVGSVGElement>>;
const PlusIcon = FiPlus as React.FC<React.SVGProps<SVGSVGElement>>;

interface FolderSectionProps {
  folder: DataRoomFolder;
  documents: DataRoomDocument[];
  onUploadClick: (folderId: string, folderName: string) => void;
  onDeleteFolder: (folderId: string) => void;
  onDeleteDocument: (docId: string) => void;
  onViewDocument: (doc: DataRoomDocument) => void;
  onRenameFolder?: (folderId: string, currentName: string) => void;
  onAddSubfolder?: (parentFolderId: string) => void;
  subfolders?: DataRoomFolder[];
  getDocumentsForFolder?: (folderId: string) => DataRoomDocument[];
}

const FolderSection: React.FC<FolderSectionProps> = ({
  folder,
  documents,
  onUploadClick,
  onDeleteFolder,
  onDeleteDocument,
  onViewDocument,
  onRenameFolder,
  onAddSubfolder,
  subfolders,
  getDocumentsForFolder,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleHeaderClick = () => {
    setIsExpanded(!isExpanded);
  };

  const handleUploadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onUploadClick(folder.id, folder.name);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDeleteFolder(folder.id);
  };

  const handleRenameClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRenameFolder) {
      onRenameFolder(folder.id, folder.name);
    }
  };

  const handleAddSubfolderClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onAddSubfolder) {
      onAddSubfolder(folder.id);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleHeaderClick();
    }
  };

  const isTopLevel = !folder.parent_folder_id;

  return (
    <div className="su-folder" style={!isTopLevel ? { marginLeft: 24, borderLeft: '2px solid rgba(255,255,255,0.06)' } : undefined}>
      <div
        className="su-folder-header"
        onClick={handleHeaderClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-expanded={isExpanded}
      >
        <div className="su-folder-info">
          <ChevronDownIcon
            className="su-folder-icon"
            style={{
              transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 0.2s',
            }}
          />
          <FolderIcon className="su-folder-icon" />
          <span className="su-folder-name">{folder.name}</span>
          <span className="su-folder-count">
            {documents.length} {documents.length === 1 ? 'file' : 'files'}
          </span>
        </div>
        <div className="su-folder-actions">
          <button
            type="button"
            className="su-folder-action-btn"
            onClick={handleUploadClick}
            title="Upload file"
            aria-label="Upload file to folder"
          >
            <UploadIcon width={14} height={14} />
            <span>Upload</span>
          </button>
          {isTopLevel && onAddSubfolder && (
            <button
              type="button"
              className="su-folder-action-btn"
              onClick={handleAddSubfolderClick}
              title="Add sub-folder"
              aria-label="Add sub-folder"
            >
              <PlusIcon width={14} height={14} />
              <span>Sub-folder</span>
            </button>
          )}
          {onRenameFolder && (
            <button
              type="button"
              className="su-folder-action-btn"
              onClick={handleRenameClick}
              title="Rename folder"
              aria-label="Rename folder"
            >
              <EditIcon width={14} height={14} />
            </button>
          )}
          {!folder.is_default && (
            <button
              type="button"
              className="su-folder-action-btn delete"
              onClick={handleDeleteClick}
              title="Delete folder"
              aria-label="Delete folder"
            >
              <TrashIcon width={14} height={14} />
            </button>
          )}
        </div>
      </div>
      {isExpanded && (
        <div className="su-folder-content">
          {documents.length === 0 && (!subfolders || subfolders.length === 0) ? (
            <div className="su-folder-empty">
              No files uploaded yet. Click &quot;Upload&quot; to add documents.
            </div>
          ) : (
            <>
              {documents.length > 0 && (
                <div className="su-document-list">
                  {documents.map((doc) => (
                    <DocumentRow
                      key={doc.id}
                      document={doc}
                      onDelete={onDeleteDocument}
                      onView={onViewDocument}
                    />
                  ))}
                </div>
              )}
              {/* Render sub-folders */}
              {subfolders && subfolders.length > 0 && getDocumentsForFolder && (
                <div style={{ paddingTop: documents.length > 0 ? 8 : 0 }}>
                  {subfolders
                    .sort((a, b) => a.position - b.position)
                    .map((subfolder) => (
                      <FolderSection
                        key={subfolder.id}
                        folder={subfolder}
                        documents={getDocumentsForFolder(subfolder.id)}
                        onUploadClick={onUploadClick}
                        onDeleteFolder={onDeleteFolder}
                        onDeleteDocument={onDeleteDocument}
                        onViewDocument={onViewDocument}
                        onRenameFolder={onRenameFolder}
                      />
                    ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FolderSection;
